// ./src/utils/api/BrandingService.ts
//
// White-label branding resolution by domain (public / unauthenticated).
//
// A single deployment can be pointed at many domains. We ask the API "who owns
// this host?" and get their branding DATA (name, support, favicon, theme). For
// the first-party hosts and localhost this is seeded in `white_label`; for a
// custom domain it comes from that host's white-label mapping. Styles stay
// local for now — only identity/data is applied (themeTokens is carried through
// for later).

import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { askDroppingRefused } from "../appBrand";

export interface DomainBranding {
  domain?: string;
  spaceId?: string;
  brandName?: string;
  supportEmail?: string;
  faviconUrl?: string;
  logoUploadedFileId?: string;
  themeId?: string;
  /** Theme design tokens — consumed later; styles are local for now. */
  themeTokens?: Record<string, unknown> | null;
  adminSubdomain?: string;
  /** ISO 3166-1 alpha-2 default country for the brand — the phone-input default region (fallback 'US'). */
  defaultCountry?: string | null;
  /** A2e #101 — the brand website (the white label's, else its space's). */
  websiteUrl?: string | null;
  /** A2e #101 — the brand logo as a plain URL (the white label's, else its space's). */
  logoUrl?: string | null;
  /** A2e #101 — the space's OWN white label (a first-party domain), not a host's custom domain. */
  isPlatform?: boolean | null;
  /** A2e #101 — the PLATFORM brand of the domain's space (the account's payee); = brandName on a first-party domain. */
  platformName?: string | null;
  /** A2e #101 — the platform brand's website (its terms and privacy policy). */
  platformWebsiteUrl?: string | null;
  /** A2e #101 — the public event website origin this domain links to (its own, else its brand's). */
  siteUrl?: string | null;
}

/**
 * What a read of this host's white label came to. `answered` false = the read FAILED (network, server) — a cached brand
 * is kept; `answered` true with `branding` null = the API says this host has no white label.
 */
export interface BrandingAnswer {
  answered: boolean;
  branding: DomainBranding | null;
}

// Public resolver on eventsmagic-api. An explicit `domain` wins; if omitted the
// server falls back to the request Referer/Origin.
// A2e #101 — the NEWER_BRAND_FIELDS are newer than some gateways: one not yet recomposed refuses the WHOLE query, so the
// read asks again without exactly the fields it refused (utils/appBrand `askDroppingRefused`) — a gateway that knows the
// older ones keeps them, and the name is never lost.
const whiteLabelByDomainQuery = (fields: readonly string[]) => gql`
  query WhiteLabelByDomain($domain: String) {
    whiteLabelByDomain(domain: $domain) {
      code
      data {
        domain
        spaceId
        brandName
        supportEmail
        faviconUrl
        logoUploadedFileId
        themeId
        themeTokens
        adminSubdomain
        defaultCountry
        ${fields.join(" ")}
      }
    }
  }
`;

export class BrandingService {
  /**
   * Read the white label of a domain. Never throws: a failed read is `answered: false` (see BrandingAnswer), so the
   * caller can keep what it remembered rather than forget the brand over a network blip.
   */
  static async resolveByDomain(domain?: string): Promise<BrandingAnswer> {
    try {
      const result = await askDroppingRefused((fields) => ApiService.executeQuery(whiteLabelByDomainQuery(fields), { domain: domain || null }));
      if (!result || result.didFail?.()) {
        return { answered: false, branding: null };
      }

      const data = result.getDataFirst?.() ?? null;

      return { answered: true, branding: data && typeof data === "object" ? (data as DomainBranding) : null };
    } catch {
      return { answered: false, branding: null };
    }
  }
}
